import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { merge, noop, isEmpty, omit } from 'lodash-es';
import uuidv4 from 'uuid/v4';
import { connect } from 'react-redux';
import Modal from 'reactstrap/lib/Modal';
import ModalBody from 'reactstrap/lib/ModalBody';
import Button from 'reactstrap/lib/Button';

import { UserProfileModel } from 'client/data/models/profile/profile';
import { connectToModel } from 'client/data/luckdragon/redux/react-binding';

import { signInAnonymously } from 'site-modules/shared/components/profile/firebase-anonymous-auth';
import { signOut } from 'site-modules/shared/components/profile/firebase-auth';
import {
  getFinancingAttributes,
  setFinancingAttributes,
} from 'client/site-modules/shared/components/profile/attributes/bundles';
import { profileScreenMethods } from 'site-modules/shared/components/profile/profile-screen-methods';
import { sameFirebaseSession } from 'site-modules/shared/components/profile/idm/idm-utils';

import { Storage } from 'client/site-modules/shared/utils/storage';
import { EventToolbox } from 'client/utils/event-toolbox';
import { IDM_METHODS } from 'client/data/models/profile/profile-constants';
import { RECORD_SIZE_WARNING_EVENT, RECORD_SIZE_ERROR_EVENT } from 'site-modules/shared/components/profile/idm/idm';

import { Link } from 'site-modules/shared/components/link/link';

const AUTHORIZATION_DISABLED_STYLES = { opacity: '0.5', pointerEvents: 'none' };
const storage = new Storage('localStorage');
const mergeFinancingIdmData = idmData => merge(idmData, getFinancingAttributes(storage));
const empty = {};

const WARNING_TITLE = 'Storage Almost Full';
const WARNING_MESSAGE =
  'Your saved data is approaching the storage limit. Please consider removing some older saved vehicles, searches, or appraisals to free up space.';
const BLOCKED_TITLE = 'Storage Limit Reached';
const BLOCKED_MESSAGE =
  'Your saved data has reached the maximum storage limit. Please delete some saved vehicles, searches, or appraisals before adding new items.';

// Tracks the instanceId of the last component that initiated an updateModel action
let currentInitiatorId = null;

export const mapStateToProps = ({ visitor, pageContext, profile }) => ({
  id: visitor?.id,
  session: visitor?.session,
  location: visitor?.location,
  pageContext,
  isAuthenticated: profile?.isAuthenticated,
  isAnonymousUser: profile?.isAnonymousUser,
  modelLinkCode: pageContext?.vehicle?.model?.modelLinkCode,
  dataIdm: profile?.data?.idm ?? empty,
});

/**
 * insiderMethods: HOC to pass common Idm methods into child components
 * @param component React Element requiring Idm methods
 * @param {Object} options
 *  @param {Boolean} waitForAuth - Collapse wrapped component until authenticated
 *  @param {Boolean} includeIdmData - Adds `profileIdmData` to props so wrapped component has access to data
 * @returns React Element function to render WrappedComponent with Idm functions and passed in props
 */
export function insiderMethods(component, options = {}) {
  /**
   * InsiderMethodsWrapper: Wrapper to provide connectToModel methods used by Idm methods
   * @param WrappedComponent React Element requiring Idm methods
   * @param wrappedProps Props to be passed into WrappedComponent
   * @param setModelValue Method provided by connectToModel used by Idm methods
   * @returns React Element rendering WrappedComponent with Idm functions and passed in props
   */
  class InsiderMethodsWrapper extends Component {
    static propTypes = {
      WrappedComponent: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
        PropTypes.string,
        PropTypes.func,
        PropTypes.elementType,
      ]).isRequired,
      setModelValue: PropTypes.func.isRequired,
      toggleSignModal: PropTypes.func.isRequired,
      wrappedProps: PropTypes.shape({}),
      pageContext: PropTypes.shape({
        page: PropTypes.shape({}),
      }),
      id: PropTypes.string,
      session: PropTypes.string,
      location: PropTypes.shape({}),
      isAuthenticated: PropTypes.bool,
      isAnonymousUser: PropTypes.bool,
      modelLinkCode: PropTypes.string,
      dataIdm: PropTypes.shape({}),
    };

    static defaultProps = {
      wrappedProps: {},
      pageContext: {},
      id: null,
      session: null,
      location: null,
      isAuthenticated: null,
      isAnonymousUser: null,
      modelLinkCode: '',
      dataIdm: {},
    };

    state = { recordSizeWarning: false, recordSizeError: false };

    componentDidMount() {
      EventToolbox.on(RECORD_SIZE_WARNING_EVENT, this.handleSizeWarning);
      EventToolbox.on(RECORD_SIZE_ERROR_EVENT, this.handleSizeError);
      this.instanceId = uuidv4();
    }

    componentDidUpdate(prevProps) {
      const { dataIdm, setModelValue } = this.props;
      const { financing } = dataIdm;
      const shouldSetFinanceData = isEmpty(prevProps.dataIdm.financing) && !isEmpty(financing);
      if (shouldSetFinanceData) setFinancingAttributes(storage, dataIdm, setModelValue);
    }

    componentWillUnmount() {
      EventToolbox.off(RECORD_SIZE_WARNING_EVENT, this.handleSizeWarning);
      EventToolbox.off(RECORD_SIZE_ERROR_EVENT, this.handleSizeError);
    }

    handleSizeWarning = () => {
      if (this.instanceId === currentInitiatorId) {
        this.setState({ recordSizeWarning: true, recordSizeError: false });
      }
    };

    handleSizeError = () => {
      if (this.instanceId === currentInitiatorId) {
        this.setState({ recordSizeError: true });
      }
    };

    dismissRecordSizeNotification = () => {
      this.setState({ recordSizeWarning: false, recordSizeError: false });
    };

    updateModel(data, method, addVisitorAndPageContext, uid, checkExisting) {
      currentInitiatorId = this.instanceId;
      const { setModelValue, id, session, location, pageContext } = this.props;
      return setModelValue('data.idm', UserProfileModel, {
        data,
        visitorAndPageContext: addVisitorAndPageContext
          ? {
              visitor: { id, session, location },
              pageContext,
            }
          : undefined,
        method,
        uid,
        checkExisting,
      });
    }

    /**
     * removeDataFromIdm - Function will also update UPS with any local storage calculator preferences (MEM-3126)
     * @param {Object} idmData - idmData to delete
     */
    removeDataFromIdm = idmData =>
      Promise.all([
        this.updateModel(mergeFinancingIdmData(), IDM_METHODS.UPDATE),
        this.updateModel(idmData, IDM_METHODS.DELETE),
      ]);

    /**
     * updateIdmData - Saves "idmData" to UPS. It should be used when saving data does not contain email
     * @param {Object} idmData - idmData to save
     * @param {Boolean} withContext
     */
    updateIdmData = (idmData, withContext) =>
      this.updateModel(mergeFinancingIdmData(idmData), IDM_METHODS.UPDATE, withContext);

    /**
     * create or update idmData to UPS. It should be used when saving data contains email
     * @param {Object} idmDataToSave - idmDataToSave to save
     * @param {Boolean} withContext - save with location and visitor context to UPS
     * @param {Object} additionalOptions
     *    @param {Object} signInAnonymouslyTracking
     *      @param {String} creativeId - creative id for sign in anonymously tracking purposes
     *      @param {Object} trackingData - additional tracking data for sign in anonymous tracking purposes
     *    @param {Array<String>} fieldsToCopyOnCreate - properties to copy from the existing profile to the new one, with new data taking precedence
     */
    createOrUpdateIdmData = async ({
      idmDataToSave,
      signInAnonymouslyTracking = {},
      withContext,
      showModal,
      skipUpdate,
      fieldsToCopyOnCreate = [],
    }) => {
      const { isAuthenticated, isAnonymousUser, dataIdm, toggleSignModal } = this.props;

      const hasNotEmail = !idmDataToSave?.identifiers?.email?.[0]; // just update profile
      const isSameFirebaseSession = hasNotEmail || sameFirebaseSession(idmDataToSave, dataIdm, true);
      if (isAuthenticated && (!isAnonymousUser || isSameFirebaseSession)) {
        // register user - safe check email
        const saveData = !isAnonymousUser ? omit(idmDataToSave, 'identifiers') : idmDataToSave;

        return skipUpdate // prevent extra calls to IDM - edge case in join-insider-sign-up.jsx
          ? Promise.resolve()
          : this.updateModel(mergeFinancingIdmData(saveData), IDM_METHODS.UPDATE, withContext);
      }

      // Copy specified properties from the existing profile to the new profile, if any are provided
      const copiedProperties = fieldsToCopyOnCreate.reduce((acc, property) => {
        if (dataIdm[property]) {
          acc[property] = dataIdm[property];
        }
        return acc;
      }, {});
      // Merge copied properties from the existing profile with the new data, giving precedence to the new data
      const mergedIdmDataToSave = { ...copiedProperties, ...idmDataToSave };

      const { creativeId = '', trackingData = {} } = signInAnonymouslyTracking;
      const toggleModal = showModal ? toggleSignModal : noop;
      // always signout before creating new anonymous account
      return signOut().then(() =>
        signInAnonymously(creativeId, trackingData)
          .then(() => this.updateModel(mergeFinancingIdmData(mergedIdmDataToSave), IDM_METHODS.CREATE_ANONYMOUS, true))
          .then(toggleModal)
      );
    };

    /**
     * updateIdmDataOnLogin - Saves to "idmData" with login attributes to UPS.
     * @param {Object} idmData - idmData to save
     * @param {String} uid - Anonymous id, if exists
     * @param {boolean} checkExisting
     */
    updateIdmDataOnLogin = (idmData, uid, checkExisting) =>
      this.updateModel(idmData, IDM_METHODS.UPDATE_LOGIN, true, uid, checkExisting);

    /**
     * createAnonymousIdmProfile - Saves "idmData" to UPS for new anonymous user. If possible please use "signInAnonymousAndSaveProfile" method below.
     * IMPORTANT: This should ONLY be used for newly "signInAnonymously" users (so it should happen right after firebase sign in anonymously).
     * @param {Object} idmData - idmData to to save
     */
    createAnonymousIdmProfile = idmData =>
      this.updateModel(mergeFinancingIdmData(idmData), IDM_METHODS.CREATE_ANONYMOUS, true);

    /**
     * @function updateToCurrentThenSignInAnonymousAndSave
     * Process:
     * 1. Updates to current profile
     * 2. Performs sign out of current profile
     * 3. Performs sign in to anonymous user (new) and saves data.
     * @param {Object} dataToUpdate - idmData to update for current user
     * @param {Object} dataToCreate - idmData to save for new anonymous user
     * @param {Object} signInAnonymouslyTracking - tracking data for `signInAnonymously`.
     */
    updateToCurrentThenSignInAnonymousAndSave = (dataToUpdate, dataToCreate, signInAnonymouslyTracking) =>
      this.updateModel(
        {
          dataToUpdate,
          dataToCreate: mergeFinancingIdmData(dataToCreate),
          signInAnonymouslyTracking,
        },
        IDM_METHODS.UPDATE_CURRENT_SIGN_IN_CREATE_ANONYMOUS,
        true
      );

    render() {
      const { WrappedComponent, wrappedProps, isAuthenticated, modelLinkCode, dataIdm } = this.props;
      const { waitForAuth, includeIdmData } = options;
      const additionalProps = {};
      if (includeIdmData) {
        additionalProps.profileIdmData = dataIdm;
      }

      const { recordSizeWarning, recordSizeError } = this.state;
      const showRecordSizeNotification = recordSizeWarning || recordSizeError;

      const componentWithProps = (
        <WrappedComponent
          removeDataFromIdm={this.removeDataFromIdm}
          updateIdmData={this.updateIdmData}
          createOrUpdateIdmData={this.createOrUpdateIdmData}
          updateIdmDataOnLogin={this.updateIdmDataOnLogin}
          createAnonymousIdmProfile={this.createAnonymousIdmProfile}
          updateToCurrentThenSignInAnonymousAndSave={this.updateToCurrentThenSignInAnonymousAndSave}
          {...wrappedProps}
          isAuthenticated={isAuthenticated}
          modelLinkCode={modelLinkCode}
          {...additionalProps}
        />
      );

      if (waitForAuth && isAuthenticated === null) {
        return (
          <span className="authorization-disabled" style={AUTHORIZATION_DISABLED_STYLES}>
            {componentWithProps}
          </span>
        );
      }

      return (
        <>
          {showRecordSizeNotification && (
            <Modal isOpen toggle={this.dismissRecordSizeNotification} centered zIndex={9999}>
              <ModalBody className="p-1_5">
                <div className="d-flex align-items-center mb-1">
                  <i
                    className={`icon-${recordSizeError ? 'cancel-circle2' : 'warning2'} size-24 me-0_75 text-${recordSizeError ? 'danger' : 'warning'}`}
                    aria-hidden
                  />
                  <h3 className="mb-0 heading-4">{recordSizeError ? BLOCKED_TITLE : WARNING_TITLE}</h3>
                </div>
                <p className="mb-1_5">{recordSizeError ? BLOCKED_MESSAGE : WARNING_MESSAGE}</p>
                <p className="mb-1_5">
                  <Link to="/insider" className="text-underline">
                    View your dashboard
                  </Link>
                </p>
                <div className="text-center">
                  <Button
                    color="primary-b"
                    size="lg"
                    className="fw-normal px-3_5 py-0_5"
                    onClick={this.dismissRecordSizeNotification}
                  >
                    OK
                  </Button>
                </div>
              </ModalBody>
            </Modal>
          )}
          {componentWithProps}
        </>
      );
    }
  }

  const ConnectedComponentWithContext = connect(mapStateToProps)(
    connectToModel(profileScreenMethods(InsiderMethodsWrapper), {})
  );

  return props => <ConnectedComponentWithContext WrappedComponent={component} wrappedProps={props} />;
}
